import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiAuthError, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select, IconPencil, IconLock } from '../atoms';
import { Checkbox } from '../atoms'; // Annual info-request campaign — enabled toggle
import { StatCard, useToast, NotificationMatrixEditor, LastUpdated } from '../organisms';
import { COMMON_TIMEZONES } from '../lib/orgTimezone';
import { formatTagLabel } from '../lib/formatTag';
import { latestAttr, type AttrMap } from '../lib/inlineAttribution';

/**
 * OrgSettings — /v2/org-settings
 *
 * The org configuration control plane. Registries backed by live endpoints —
 * document types, contact types, and contact roles — are editable here
 * (super_admin only, enforced server-side). Contact Types (the WHAT a contact
 * is) and Contact Roles (the WHY it's attached) are two parallel registries that
 * both drive the add-contact form. White-label branding (display name, brand colors,
 * outbound-email identity, and the org logo) is a live editor wired to
 * GET/PUT /api/branding + POST /api/branding/logo (admin tier, enforced
 * server-side); the logo bytes are stored in R2/S3 and served back through the
 * org-scoped /api/branding/logo route.
 *
 * Registry writes go through PUT /api/org-settings/{document-types,contact-types,contact-roles}
 * with bodies { document_types: [...] } / { contact_types: [...] } / { contact_roles: [...] }.
 *
 * The registries this page offers are exactly the ones `src/config/org-settings.js`
 * defines. The inherited property-tax sections (Properties, Tax returns,
 * Government, appeal stage templates) were removed along with their backend
 * settings — they configured tables this product does not have.
 *
 * Domain 03.4 — RBAC, Identity & Org Settings.
 */

interface CurrentUser {
  id: string;
  role: string;
  org_name: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
  logo_url: string | null;
}

interface Org {
  id: string;
  name: string;
  current_system_year: number | null;
  country: string | null;
  status: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
}

export function OrgSettings() {
  const userQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  const orgQuery = useQuery<Org[]>({
    queryKey: ['organizations'],
    queryFn: ({ signal }) => api.get<Org[]>('/api/organizations', signal),
  });

  const contactRolesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-roles'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-roles', signal),
  });

  const contactTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-types', signal),
  });

  const documentTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'document-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/document-types', signal),
  });

  const clientTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'client-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/client-types', signal),
  });
  const clientCategoriesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'client-categories'],
    queryFn: ({ signal }) => api.get('/api/org-settings/client-categories', signal),
  });

  if (userQuery.isLoading || orgQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  }

  // Role gate
  if (userQuery.data && userQuery.data.role !== 'super_admin' && userQuery.data.role !== 'admin') {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>Org Settings are restricted to super_admin and admin roles.</p>
      </div>
    );
  }

  const org = orgQuery.data?.[0];
  if (!org) return <p style={{ color: 'var(--color-ink-3)' }}>No organization on file.</p>;

  // Registry writes are super_admin only (the backend enforces this too); admins
  // see the registries read-only.
  const canEditRegistries = userQuery.data?.role === 'super_admin';
  const contactRoles = normalizeValues(contactRolesQuery.data);
  const contactTypes = normalizeValues(contactTypesQuery.data);
  const documentTypes = normalizeValues(documentTypesQuery.data);
  const clientTypes = normalizeValues(clientTypesQuery.data);
  const clientCategories = normalizeValues(clientCategoriesQuery.data);

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Settings</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Configuration for <strong>{org.name}</strong>. Branding is editable by admins; document types and contact roles are editable{canEditRegistries ? '' : ' by a super_admin'}.
        </p>
      </header>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-6)',
      }}>
        {/* organizations.current_system_year is a real column and is READ by live
            code — services/annual-info-request.js, services/document-request.js
            and services/onboarding.js all default their year to it. So it is
            relabelled, not dropped: for this org it is the PROGRAMME YEAR that
            cohorts, enrollments and the annual document refresh are stamped
            against. */}
        <StatCard label="Programme year" value={org.current_system_year ? String(org.current_system_year) : '—'} caption="cohorts + annual requests" accent="hunter" />
        <StatCard label="Country" value={org.country || '—'} caption="locale" accent="info" />
        <StatCard label="Status" value={org.status || 'active'} caption="org row" accent="green" />
        <StatCard label="Contact roles" value={String(contactRoles.length)} caption="registry entries" accent="amber" />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 'var(--sp-4)',
        alignItems: 'start',
      }}>
        <BrandingEditor canEdit />

        <TimezoneEditor canEdit />

        {/* Contacts — the two parallel registries (type = WHAT, role = HOW)
            combined under one section. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Contacts</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The vocabulary for the people and organizations attached to your records. A contact <strong>type</strong> is what the person is; a contact <strong>role</strong> is how they're attached to a given record. Both feed the dropdowns on every Contacts tab.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)', alignItems: 'start' }}>
            <RegistryEditor
              title="Contact types"
              slug="contact-types"
              bodyField="contact_types"
              values={contactTypes}
              loading={contactTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a contact type"
              description="What a person IS — e.g. business owner or rep, ecosystem partner rep, buyer rep, advisor. Offered whenever a contact is added to a record."
            />
            <RegistryEditor
              title="Contact roles"
              slug="contact-roles"
              bodyField="contact_roles"
              values={contactRoles}
              loading={contactRolesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a contact role"
              description="How a person is attached to a specific record — e.g. primary, owner, founder, invoicing. Set per link, so one person can hold different roles on different records."
            />
          </div>
        </div>

        {/* 7/28 C8: Document Types is its OWN section (was a bare editor jammed after
            Contacts), with room to spread out. It's the single master list every surface
            reads from — internal All Documents, the portal, and every generator. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Documents</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The single master list of document types used everywhere — the internal All Documents tab, the portal, and every upload and system-generated document. Locked money-bearing types (Invoice) can't be renamed or removed, so their download-redaction gate can't be broken.
          </p>
          <RegistryEditor
            title="Document types"
            slug="document-types"
            bodyField="document_types"
            values={documentTypes}
            loading={documentTypesQuery.isLoading}
            canEdit={canEditRegistries}
            addPlaceholder="Add a document type"
            description="The categories a document can be filed under across the app (e.g. Capability Statement, Business License, Financial Statement). Drives the Document Type dropdown on every upload."
          />

          {/* Item 2.3 — flag which document types auto-share to the portal.
              The master list above is split into two buckets; a type auto-shares
              (the business is notified on upload) when it sits in the left bucket. */}
          <div style={{ marginTop: 'var(--sp-4)' }}>
            <DocumentAutoShareEditor
              documentTypes={documentTypes}
              documentTypesLoading={documentTypesQuery.isLoading}
              canEdit={canEditRegistries}
            />
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Businesses</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The vocabulary for the business profile Identity and Contract boxes. Renaming a value updates every business already tagged with it — these two lists span thousands of records, so each carries its own edit history.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)', alignItems: 'start' }}>
            <RegistryEditor
              title="Business types"
              slug="client-types"
              bodyField="client_types"
              values={clientTypes}
              loading={clientTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a business type"
              description="The KIND of business record — e.g. Participant, Coalition Member, Referral Partner. Offered as the Type dropdown on the business profile Identity box."
              attribution={{ entityId: 'client-types', field: 'client_types' }}
            />
            <RegistryEditor
              title="Business categories"
              slug="client-categories"
              bodyField="client_categories"
              values={clientCategories}
              loading={clientCategoriesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a business category"
              description="The industry a business operates in — e.g. Food & Beverage, Construction, Not For Profit. Offered as the Category dropdown on the business profile Identity box."
              attribution={{ entityId: 'client-categories', field: 'client_categories' }}
            />
          </div>
        </div>

        {/* ── Annual info-request campaign ── */}
        <AnnualInfoRequestEditor canEdit={canEditRegistries} />

        {/* ── Notification matrix (feature 41) — full-width grid ── */}
        <NotificationMatrixEditor canEdit={canEditRegistries} />

      </div>

      {/* ── Portal (feature #35) — the portal configuration hub ── */}
      <section style={{ marginTop: 'var(--sp-6)' }}>
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--fs-xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>Portal</h2>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>
            What businesses can do in the portal — e-signature, document uploads, and online payment. Document types are managed once in the master Document Types list above.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 'var(--sp-4)',
        }}>
          <PortalSettingsEditor canEdit={canEditRegistries} />
        </div>
      </section>
    </>
  );
}

/**
 * BrandingSettings — the shape GET /api/branding resolves (org row merged over
 * platform defaults; every field is always present).
 */
interface BrandingSettings {
  display_name: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  email_from_name: string;
  email_footer: string;
  terminology_overrides: Record<string, string>;
  ai_enabled: boolean;
}

/**
 * BrandingEditor — the live white-label editor. Reads the org's effective
 * branding from GET /api/branding and persists edits via PUT /api/branding
 * (display name, brand colors, outbound-email identity) and the org logo via a
 * multipart POST /api/branding/logo (bytes → R2/S3, served back org-scoped).
 * On any successful write it invalidates the shared ['branding'] query so the
 * operator shell (useBranding) re-themes immediately.
 */
function BrandingEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const brandingQuery = useQuery<BrandingSettings>({
    queryKey: ['branding'],
    queryFn: ({ signal }) => api.get<BrandingSettings>('/api/branding', signal),
    retry: false,
  });

  // Working copy of the editable fields; null until first edit → falls back to
  // the persisted values. A field-level draft keyed by column name.
  type Draft = Pick<BrandingSettings, 'display_name' | 'primary_color' | 'accent_color' | 'email_from_name' | 'email_footer'>;
  const [draft, setDraft] = useState<Partial<Draft>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Cache-buster so the <img> refetches after a new logo is uploaded.
  const [logoVersion, setLogoVersion] = useState(0);

  const persisted = brandingQuery.data;

  if (brandingQuery.isLoading) {
    return (
      <InfoCard label="Branding (white-label)">
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      </InfoCard>
    );
  }
  if (!persisted) {
    return (
      <InfoCard label="Branding (white-label)">
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>Branding unavailable.</p>
      </InfoCard>
    );
  }

  const field = <K extends keyof Draft>(k: K): string => (draft[k] ?? persisted[k]) as string;
  const setField = <K extends keyof Draft>(k: K, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const dirty = (Object.keys(draft) as (keyof Draft)[]).some((k) => draft[k] !== undefined && draft[k] !== persisted[k]);

  async function save() {
    setSaving(true);
    try {
      const body: Partial<Draft> = {};
      for (const k of Object.keys(draft) as (keyof Draft)[]) {
        if (draft[k] !== undefined && draft[k] !== persisted![k]) body[k] = draft[k];
      }
      await api.put('/api/branding', body);
      await queryClient.invalidateQueries({ queryKey: ['branding'] });
      setDraft({});
      toast({ variant: 'success', message: 'Branding saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  async function onLogoPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    try {
      // Multipart upload — the JSON api client can't send FormData, so use fetch
      // directly (same-origin cookies + the correlation header, like api.ts).
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/branding/logo', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'x-request-id': reqId() },
        body: fd,
      });
      if (!res.ok) {
        let msg = `Upload failed (HTTP ${res.status}).`;
        try { const b = await res.json(); if (b && b.error) msg = String(b.error); } catch { /* keep default */ }
        throw new Error(msg);
      }
      await queryClient.invalidateQueries({ queryKey: ['branding'] });
      setLogoVersion((v) => v + 1);
      toast({ variant: 'success', message: 'Logo uploaded.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Logo upload failed.') });
    } finally {
      setUploading(false);
    }
  }

  const hasLogo = !!persisted.logo_url;
  const logoSrc = hasLogo ? `/api/branding/logo?v=${logoVersion}` : null;

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Branding (white-label)</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
        <div style={{
          width: 64, height: 64, flexShrink: 0,
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {logoSrc ? (
            <img src={logoSrc} alt="Org logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>No logo</span>
          )}
        </div>
        {canEdit && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
              onChange={onLogoPicked}
              style={{ display: 'none' }}
            />
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : hasLogo ? 'Replace logo' : 'Upload logo'}
            </Button>
            <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              PNG, JPEG, SVG, WebP or GIF · up to 2MB
            </p>
          </div>
        )}
      </div>

      <FieldRow label="Display name">
        <Input value={field('display_name')} disabled={!canEdit} onChange={(e) => setField('display_name', e.target.value)} />
      </FieldRow>

      <FieldRow label="Primary color">
        <ColorField value={field('primary_color')} disabled={!canEdit} onChange={(v) => setField('primary_color', v)} />
      </FieldRow>

      <FieldRow label="Accent color">
        <ColorField value={field('accent_color')} disabled={!canEdit} onChange={(v) => setField('accent_color', v)} />
      </FieldRow>

      <FieldRow label="Email from-name">
        <Input value={field('email_from_name')} disabled={!canEdit} onChange={(e) => setField('email_from_name', e.target.value)} />
      </FieldRow>

      <FieldRow label="Email footer">
        {/* The placeholder shows the platform default this org falls back to when
            the field is cleared — it must match src/services/branding.js's
            email_footer default, or the field advertises a footer that outbound
            mail never actually carries. */}
        <textarea
          value={field('email_footer')}
          placeholder="Sent by IGNITE! Central."
          disabled={!canEdit}
          onChange={(e) => setField('email_footer', e.target.value)}
          rows={2}
          style={{
            fontFamily: 'inherit',
            fontSize: 'var(--fs-md)',
            padding: '8px 12px',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            background: 'var(--color-card)',
            color: 'var(--color-ink)',
            outline: 'none',
            width: '100%',
            resize: 'vertical',
          }}
        />
      </FieldRow>

      <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        Colors apply app-wide via the <code style={{ fontFamily: 'var(--font-mono)' }}>--brand-*</code> tokens; the from-name and footer brand outbound email.
      </p>
    </div>
  );
}

/**
 * TimezoneEditor — the org's IANA timezone (organizations.timezone, a real
 * column). Reads GET /api/org-settings/timezone and persists via PUT (admin tier,
 * enforced server-side). This zone cross-cuts the whole product: the Calendar
 * renders event times and reminders in it, and reminder fire-times are computed
 * against it — so on save we invalidate ['current-user'] (which now carries
 * `timezone`) so the calendar re-themes to the new zone immediately.
 */
function TimezoneEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const tzQuery = useQuery<{ timezone: string }>({
    queryKey: ['org-settings', 'timezone'],
    queryFn: ({ signal }) => api.get<{ timezone: string }>('/api/org-settings/timezone', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const persisted = tzQuery.data?.timezone ?? '';
  const value = draft ?? persisted;
  const dirty = draft !== null && draft !== persisted;

  // Merge any persisted zone that isn't in the curated list so it still shows.
  const options = COMMON_TIMEZONES.some((o) => o.value === persisted) || !persisted
    ? COMMON_TIMEZONES
    : [{ value: persisted, label: persisted }, ...COMMON_TIMEZONES];

  async function save() {
    setSaving(true);
    try {
      await api.put('/api/org-settings/timezone', { timezone: value });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'timezone'] });
      // The calendar reads the org tz off /api/current-user — refresh it so
      // event/reminder rendering picks up the new zone without a reload.
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Timezone saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Organization timezone</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {tzQuery.isLoading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <>
          <Select
            options={options}
            value={value}
            disabled={!canEdit}
            onChange={(e) => setDraft(e.target.value)}
          />
          <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Drives Calendar date/time rendering and reminder fire-times. An 8:00 reminder fires at 08:00 in this zone.
          </p>
        </>
      )}
    </div>
  );
}

/** A labeled control row for the branding editor. */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--sp-3)' }}>
      <label style={{
        display: 'block',
        marginBottom: 'var(--sp-1)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--color-ink-3)',
      }}>{label}</label>
      {children}
    </div>
  );
}

/** A color swatch (native picker) paired with a hex text input, kept in sync. */
function ColorField({ value, disabled, onChange }: { value: string; disabled: boolean; onChange: (v: string) => void }) {
  // The native <input type="color"> needs a valid #rrggbb; fall back for odd values.
  const swatch = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
      <input
        type="color"
        value={swatch}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Color picker"
        style={{
          width: 36, height: 36, padding: 0,
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
          background: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
      <Input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ fontFamily: 'var(--font-mono)' }}
      />
    </div>
  );
}

/** Extract a human message from an ApiClientError body or Error. */
function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body) {
    return String((err.body as { error: string }).error);
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Correlation id for the raw multipart fetch (mirrors lib/api.ts). */
function reqId(): string {
  try { return crypto.randomUUID(); } catch { return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
}

/**
 * humanizeLabel — render a taxonomy value with spaces + title case for DISPLAY
 * (retest B4: "Business Plan", not "business_plan"). Only transforms
 * slug-form values (those containing an underscore) — a value already in
 * display-name form is returned untouched so a small word is not wrongly
 * re-capitalized ("Certificate of Insurance" stays "Certificate of Insurance",
 * not "Certificate Of Insurance"). The STORED value is never changed; only the
 * rendered text.
 */
// 7/28 A5: org settings is THE formatting standard — every tag reads first-letter
// capitalized + no underscores, including a lowercase no-underscore value like "attorney"
// (which the old body left lowercase). Delegate to the single shared formatter so org
// settings, the contact badges, and the portal all render tags identically.
function humanizeLabel(v: string): string {
  return formatTagLabel(v);
}

/**
 * RegistryEditor — chip list with add/remove + save for a string-array org
 * setting. Tracks a working copy against the persisted values; Save is enabled
 * only when the set differs. Persists via PUT /api/org-settings/:slug and
 * invalidates the cached query on success.
 */
function RegistryEditor({
  title, slug, bodyField, values, loading, canEdit, addPlaceholder, description, systemValues, attribution,
}: {
  title: string;
  slug: string;
  bodyField: string;
  values: string[];
  loading: boolean;
  canEdit: boolean;
  addPlaceholder: string;
  description?: string;
  /** Protected built-in values — can be reordered but not renamed or removed. */
  systemValues?: string[];
  /** When set, show a per-registry "last edited by · when" line, read from the
   *  shared field-attribution infra (audit_logs, entity_type='org_settings').
   *  `entityId` is the registry slug; `field` is its storageKey. */
  attribution?: { entityId: string; field: string };
}) {
  const isLocked = (v: string) => !!systemValues && systemValues.includes(v);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  // Last edited (optional) — reuse GET /api/field-attribution/org_settings/:slug
  // + the LastUpdated organism. Disabled when this registry isn't audited, so the
  // other RegistryEditors are unaffected. A read failure (e.g. a non-super_admin
  // with no org_settings permission) resolves to nothing rather than an error box.
  const attrKey = ['field-attribution', 'org_settings', attribution?.entityId];
  const attrQuery = useQuery<AttrMap>({
    queryKey: attrKey,
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/org_settings/${attribution!.entityId}`, signal),
    enabled: !!attribution,
    retry: false,
  });
  const lastEdited = attribution ? latestAttr(attrQuery.data ?? {}, [attribution.field]) : null;

  // The working copy defaults to the persisted values until the user edits.
  const working = draft ?? values;
  const dirty = draft !== null && !sameSet(draft, values);

  function addValue() {
    const v = newValue.trim();
    if (!v) return;
    if (working.some(x => x.toLowerCase() === v.toLowerCase())) {
      toast({ variant: 'error', message: `"${v}" is already in the list.` });
      return;
    }
    setDraft([...working, v]);
    setNewValue('');
  }

  function removeValue(v: string) {
    if (isLocked(v)) return; // built-in — protected from removal
    setDraft(working.filter(x => x !== v));
  }

  // §6 — rename an existing value in place. Unlike add/remove (batched into the
  // draft + Save), a rename is an IMMEDIATE server op: it needs the OLD value to
  // cascade the rename to every record already tagged with it, so no stale
  // labels are left behind. Only offered when there are no pending draft edits.
  async function renameValue(from: string) {
    const to = editText.trim();
    if (!to || to === from) { setEditingValue(null); return; }
    if (working.some(x => x !== from && x.toLowerCase() === to.toLowerCase())) {
      toast({ variant: 'error', message: `"${to}" is already in the list.` });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put<{ updated?: number }>(`/api/org-settings/${slug}/rename`, { from, to });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', slug] });
      if (attribution) await queryClient.invalidateQueries({ queryKey: attrKey });
      setEditingValue(null);
      setDraft(null); // reflect server truth (rename already persisted)
      const n = res && typeof res.updated === 'number' ? res.updated : 0;
      toast({ variant: 'success', message: `Renamed to "${to}"${n ? ` · ${n} record${n === 1 ? '' : 's'} updated` : ''}.` });
    } catch (err) {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Rename failed.';
      toast({ variant: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (working.length === 0) {
      toast({ variant: 'error', message: 'Keep at least one entry.' });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/org-settings/${slug}`, { [bodyField]: working });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', slug] });
      if (attribution) await queryClient.invalidateQueries({ queryKey: attrKey });
      setDraft(null);
      toast({ variant: 'success', message: `${title} saved.` });
    } catch (err) {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Save failed.';
      toast({ variant: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>{title} ({working.length})</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {description && (
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal, 1.5)' }}>
          {description}
        </p>
      )}

      {loading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : working.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>None configured.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
          {working.map(v => (
            editingValue === v ? (
              <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
                <Input
                  value={editText}
                  autoFocus
                  aria-label={`Rename ${v}`}
                  onChange={e => setEditText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); renameValue(v); }
                    if (e.key === 'Escape') { e.preventDefault(); setEditingValue(null); }
                  }}
                  style={{ width: 160 }}
                />
                <Button variant="primary" size="sm" onClick={() => renameValue(v)} disabled={saving}>Save</Button>
                <Button variant="secondary" size="sm" onClick={() => setEditingValue(null)} disabled={saving}>Cancel</Button>
              </span>
            ) : (
              <span key={v} style={{ display: 'inline-flex' }}>
                <Badge variant="neutral">
                  {/* B4 — display the label with spaces, never the raw slug
                      ("Business Plan", not "business_plan"). The stored
                      value (v) is unchanged; only the rendered text is humanized. */}
                  {humanizeLabel(v)}
                  {/* Built-in system value — a line lock (no emoji, retest B4). */}
                  {isLocked(v) && (
                    <span
                      title="Built-in value — can be reordered but not renamed or removed"
                      aria-label="Locked built-in value"
                      style={{ marginLeft: 'var(--sp-1)', color: 'var(--color-ink-3)', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
                    ><IconLock size={11} /></span>
                  )}
                  {/* §6 — rename in place (edits every tagged record). Hidden while
                      there are unsaved add/remove edits, so the two don't collide. */}
                  {canEdit && !dirty && !isLocked(v) && (
                    <button
                      type="button"
                      onClick={() => { setEditingValue(v); setEditText(v); }}
                      aria-label={`Edit ${v}`}
                      title={`Rename "${v}" (updates every record using it)`}
                      style={{
                        marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                        color: 'var(--color-ink-3)', cursor: 'pointer', lineHeight: 1, padding: 0,
                        display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle',
                      }}
                    ><IconPencil size={11} /></button>
                  )}
                  {canEdit && !editingValue && !isLocked(v) && (
                    <button
                      type="button"
                      onClick={() => removeValue(v)}
                      aria-label={`Remove ${v}`}
                      style={{
                        marginLeft: 'var(--sp-1)',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-ink-3)',
                        cursor: 'pointer',
                        fontSize: 'var(--fs-sm)',
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >×</button>
                  )}
                </Badge>
              </span>
            )
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
          <Input
            value={newValue}
            placeholder={addPlaceholder}
            onChange={e => setNewValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addValue(); } }}
          />
          <Button variant="secondary" size="sm" onClick={addValue} disabled={!newValue.trim()}>
            Add
          </Button>
        </div>
      )}

      {/* Last edited by · when — reused attribution infra (audit_logs +
          LastUpdated), shown only for registries that carry an audited history. */}
      {lastEdited && (lastEdited.by || lastEdited.at) && (
        <div style={{ marginTop: 'var(--sp-3)', textAlign: 'right' }}>
          <LastUpdated updatedBy={lastEdited.by} updatedAt={lastEdited.at} />
        </div>
      )}
    </div>
  );
}

/**
 * DocumentAutoShareEditor — item 2.3. Presents the master document-types list
 * split into two labeled buckets — "Auto-shares to the portal" and "Manual
 * only" — and lets a super_admin move a type between them to toggle its
 * auto-share membership. Auto-share means the type flips notify_client=TRUE on
 * upload (the business is notified); the READ path lives in src/routes/documents.js.
 *
 * The membership set is its OWN live config (GET/PUT /api/document-auto-share-types)
 * — the SAME query key the upload modal reads, so a move here refreshes the
 * modal's "will be shared" hint. The master list stays editable via the sibling
 * RegistryEditor; this only flags which of those types auto-share.
 *
 * Two ways to move a type (both write the same PUT): drag a chip between buckets
 * (HTML5 draggable + onDragStart/onDragOver/onDrop — no external DnD lib, CSP-safe),
 * or use the per-chip "→ move" affordance (a real <button>, so it is keyboard- and
 * screen-reader-operable — the accessible fallback to drag).
 */
function DocumentAutoShareEditor({
  documentTypes, documentTypesLoading, canEdit,
}: {
  documentTypes: string[];
  documentTypesLoading: boolean;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [dragType, setDragType] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<'auto' | 'manual' | null>(null);

  const autoShareQuery = useQuery<string[]>({
    queryKey: ['document-auto-share-types'],
    queryFn: ({ signal }) => api.get<string[]>('/api/document-auto-share-types', signal),
    retry: false,
  });

  const loading = documentTypesLoading || autoShareQuery.isLoading;
  // The live auto-share set. Partition the MASTER list by membership so the two
  // buckets always add up to the master document-types list.
  const autoShareSet = new Set(autoShareQuery.data ?? []);
  const autoTypes = documentTypes.filter((t) => autoShareSet.has(t));
  const manualTypes = documentTypes.filter((t) => !autoShareSet.has(t));

  // Compute the next set from the current one (preserving any stored auto-share
  // entry not in the master list) and PUT it. A no-op move (already in the target
  // bucket) is ignored.
  async function move(type: string, toAutoShare: boolean) {
    if (!canEdit || saving) return;
    if (autoShareSet.has(type) === toAutoShare) return;
    const next = toAutoShare
      ? [...autoShareSet, type]
      : [...autoShareSet].filter((t) => t !== type);
    setSaving(true);
    try {
      await api.put('/api/document-auto-share-types', { document_types: next });
      await queryClient.invalidateQueries({ queryKey: ['document-auto-share-types'] });
      toast({
        variant: 'success',
        message: toAutoShare
          ? `"${type}" now auto-shares to the portal.`
          : `"${type}" is now manual only.`,
      });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  function onDrop(toAutoShare: boolean) {
    return (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(null);
      // Prefer the dataTransfer payload (survives re-renders); fall back to state.
      const type = e.dataTransfer.getData('text/plain') || dragType;
      setDragType(null);
      if (type) move(type, toAutoShare);
    };
  }

  const bucket = (
    kind: 'auto' | 'manual',
    label: string,
    hint: string,
    types: string[],
  ) => {
    const toAutoShare = kind === 'auto';
    return (
      <div
        role="group"
        onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOver(kind); } : undefined}
        onDragLeave={canEdit ? () => setDragOver((cur) => (cur === kind ? null : cur)) : undefined}
        onDrop={canEdit ? onDrop(toAutoShare) : undefined}
        aria-label={label}
        style={{
          border: `1px ${dragOver === kind ? 'solid' : 'dashed'} ${dragOver === kind ? 'var(--brand-primary)' : 'var(--color-border)'}`,
          background: dragOver === kind ? 'var(--brand-primary-bg)' : 'var(--color-bg)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-3)',
          minHeight: 96,
          transition: 'background 120ms, border-color 120ms',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
          <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{label}</span>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>({types.length})</span>
        </div>
        <p style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{hint}</p>
        {types.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
            {canEdit ? 'Drag a type here, or use the move control.' : 'None.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
            {types.map((t) => (
              <span
                key={t}
                draggable={canEdit && !saving}
                onDragStart={canEdit ? (e) => { e.dataTransfer.setData('text/plain', t); e.dataTransfer.effectAllowed = 'move'; setDragType(t); } : undefined}
                onDragEnd={() => { setDragType(null); setDragOver(null); }}
                style={{ display: 'inline-flex', cursor: canEdit && !saving ? 'grab' : 'default', opacity: dragType === t ? 0.5 : 1 }}
              >
                <Badge variant={toAutoShare ? 'success' : 'neutral'}>
                  {humanizeLabel(t)}
                  {canEdit && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => move(t, !toAutoShare)}
                      aria-label={toAutoShare ? `Move ${t} to manual only` : `Move ${t} to auto-share`}
                      title={toAutoShare ? `Move "${t}" to manual only` : `Move "${t}" to auto-share`}
                      style={{
                        marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                        color: 'var(--color-ink-3)', cursor: saving ? 'default' : 'pointer',
                        fontSize: 'var(--fs-xs)', lineHeight: 1, padding: 0, whiteSpace: 'nowrap',
                      }}
                    >{toAutoShare ? '→ Manual' : '→ Auto-share'}</button>
                  )}
                </Badge>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-1)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>Portal auto-share</h3>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal, 1.5)' }}>
        A document type in <strong>Auto-shares to the portal</strong> is sent to the portal automatically on upload — the business is notified, no extra step. Everything else is <strong>Manual only</strong>. Drag a type between the buckets (or use its move control) to change it.
      </p>

      {loading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : documentTypes.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>Add a document type above first.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 'var(--sp-3)', alignItems: 'stretch' }}>
          {bucket('auto', 'Auto-shares to the portal', 'Sent to the portal + the business notified on upload.', autoTypes)}
          {bucket('manual', 'Manual only', 'Stays internal until someone shares it.', manualTypes)}
        </div>
      )}
    </div>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every(x => bs.has(x));
}

function normalizeValues(data: string[] | { values: string[] } | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.values || [];
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

/**
 * The annual info-request campaign config (GET/PUT /api/org-settings/annual-info-request).
 * A composite object: enabled + lead_time_days + document_types + anchor_date. The
 * campaign (backend) raises one information request per active business each year,
 * `lead_time_days` ahead of `anchor_date`, with `document_types` as the requested
 * items. Frequency is annual (fixed).
 */
interface AnnualInfoRequestConfig {
  enabled: boolean;
  lead_time_days: number;
  document_types: string[];
  anchor_date: string | null;
}

/**
 * AnnualInfoRequestEditor — the Org Settings card for the automated annual
 * information-request campaign. Toggles the campaign on/off, sets how far ahead of
 * the deadline it fires, edits the document-type checklist (the requested items),
 * and sets the yearly deadline anchor (MM-DD). super_admin only (server-enforced).
 */
function AnnualInfoRequestEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const cfgQuery = useQuery<AnnualInfoRequestConfig>({
    queryKey: ['org-settings', 'annual-info-request'],
    queryFn: ({ signal }) => api.get<AnnualInfoRequestConfig>('/api/org-settings/annual-info-request', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<AnnualInfoRequestConfig | null>(null);
  const [newDocType, setNewDocType] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingDoc, setEditingDoc] = useState<string | null>(null);
  const [editDocText, setEditDocText] = useState('');

  const persisted = cfgQuery.data;
  const value = draft ?? persisted ?? null;

  const dirty = draft !== null && persisted != null && (
    draft.enabled !== persisted.enabled ||
    draft.lead_time_days !== persisted.lead_time_days ||
    (draft.anchor_date || '') !== (persisted.anchor_date || '') ||
    !sameSet(draft.document_types, persisted.document_types)
  );

  function edit(patch: Partial<AnnualInfoRequestConfig>) {
    setDraft({ ...(value as AnnualInfoRequestConfig), ...patch });
  }

  function addDocType() {
    const v = newDocType.trim();
    if (!v || !value) return;
    if (value.document_types.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast({ variant: 'error', message: `"${v}" is already in the list.` });
      return;
    }
    edit({ document_types: [...value.document_types, v] });
    setNewDocType('');
  }

  // B4 — rename a document type in place. Like the RegistryEditor rename, this is
  // an IMMEDIATE server op (not batched into the draft) because it must cascade
  // the rename to every already-raised requested item tagged with it. Offered
  // only when there are no pending draft edits, so the two never collide.
  async function renameDocType(from: string) {
    const to = editDocText.trim();
    if (!to || to === from) { setEditingDoc(null); return; }
    if (value && value.document_types.some((x) => x !== from && x.toLowerCase() === to.toLowerCase())) {
      toast({ variant: 'error', message: `"${to}" is already in the list.` });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put<{ updated?: number }>('/api/org-settings/annual-info-request/rename-document-type', { from, to });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'annual-info-request'] });
      setEditingDoc(null);
      setDraft(null); // reflect server truth (rename already persisted)
      const n = res && typeof res.updated === 'number' ? res.updated : 0;
      toast({ variant: 'success', message: `Renamed to "${to}"${n ? ` · ${n} request${n === 1 ? '' : 's'} updated` : ''}.` });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Rename failed.') });
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!value) return;
    if (value.document_types.length === 0) {
      toast({ variant: 'error', message: 'Keep at least one document type.' });
      return;
    }
    if (value.anchor_date && !/^(?:\d{4}-)?\d{2}-\d{2}$/.test(value.anchor_date)) {
      toast({ variant: 'error', message: 'Deadline must be MM-DD (e.g. 04-15).' });
      return;
    }
    setSaving(true);
    try {
      await api.put('/api/org-settings/annual-info-request', {
        annual_info_request: {
          enabled: value.enabled,
          lead_time_days: value.lead_time_days,
          document_types: value.document_types,
          anchor_date: value.anchor_date || null,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'annual-info-request'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Annual information request saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Annual information request</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {cfgQuery.isLoading || !value ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <>
          <div style={{ marginBottom: 'var(--sp-3)' }}>
            <Checkbox
              label="Enable the annual campaign"
              description="Raise one information request per active business each year, ahead of the deadline."
              checked={value.enabled}
              disabled={!canEdit}
              onChange={(e) => edit({ enabled: e.target.checked })}
            />
          </div>

          <FieldRow label="Lead time (days before deadline)">
            <Input
              type="number"
              min={0}
              max={366}
              value={String(value.lead_time_days)}
              disabled={!canEdit}
              onChange={(e) => edit({ lead_time_days: Math.max(0, Math.min(366, Number(e.target.value) || 0)) })}
            />
          </FieldRow>

          <FieldRow label="Deadline (MM-DD)">
            <Input
              placeholder="04-15"
              value={value.anchor_date ?? ''}
              disabled={!canEdit}
              onChange={(e) => edit({ anchor_date: e.target.value.trim() === '' ? null : e.target.value.trim() })}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </FieldRow>

          <label style={{
            display: 'block',
            marginBottom: 'var(--sp-1)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--color-ink-3)',
          }}>Document types requested ({value.document_types.length})</label>
          {value.document_types.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--color-muted)' }}>None configured.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
              {value.document_types.map((v) => (
                editingDoc === v ? (
                  <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
                    <Input
                      value={editDocText}
                      autoFocus
                      aria-label={`Rename ${v}`}
                      onChange={(e) => setEditDocText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); renameDocType(v); }
                        if (e.key === 'Escape') { e.preventDefault(); setEditingDoc(null); }
                      }}
                      style={{ width: 200 }}
                    />
                    <Button variant="primary" size="sm" onClick={() => renameDocType(v)} disabled={saving}>Save</Button>
                    <Button variant="secondary" size="sm" onClick={() => setEditingDoc(null)} disabled={saving}>Cancel</Button>
                  </span>
                ) : (
                  <span key={v} style={{ display: 'inline-flex' }}>
                    <Badge variant="neutral">
                      {v}
                      {/* B4 — rename in place (cascades to every tagged request).
                          Hidden while there are unsaved add/remove edits so the
                          immediate rename and the batched draft don't collide. */}
                      {canEdit && !dirty && (
                        <button
                          type="button"
                          onClick={() => { setEditingDoc(v); setEditDocText(v); }}
                          aria-label={`Edit ${v}`}
                          title={`Rename "${v}" (updates every request using it)`}
                          style={{
                            marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                            color: 'var(--color-ink-3)', cursor: 'pointer', lineHeight: 1, padding: 0,
                            display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle',
                          }}
                        ><IconPencil size={11} /></button>
                      )}
                      {canEdit && !editingDoc && (
                        <button
                          type="button"
                          onClick={() => edit({ document_types: value.document_types.filter((x) => x !== v) })}
                          aria-label={`Remove ${v}`}
                          style={{
                            marginLeft: 'var(--sp-1)',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--color-ink-3)',
                            cursor: 'pointer',
                            fontSize: 'var(--fs-sm)',
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >×</button>
                      )}
                    </Badge>
                  </span>
                )
              ))}
            </div>
          )}

          {canEdit && (
            <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
              <Input
                value={newDocType}
                placeholder="Add a document type"
                onChange={(e) => setNewDocType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDocType(); } }}
              />
              <Button variant="secondary" size="sm" onClick={addDocType} disabled={!newDocType.trim()}>
                Add
              </Button>
            </div>
          )}

          <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Frequency is annual. Each request writes the deadline to the calendar and notifies the business's portal users.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * PortalSettings — the composite of portal-wide toggles (feature #35).
 * GET/PUT /api/org-settings/portal-settings.
 */
interface PortalSettingsConfig {
  esignature_enabled: boolean;
  allow_document_upload: boolean;
  allow_online_payment: boolean;
}

// The former fourth toggle, `show_appeal_status`, was REMOVED rather than
// relabelled: nothing in this product has an appeal, and no journey-status
// portal surface exists yet for it to gate. A toggle that governs nothing is
// worse than an absent one — it reads as a shipped feature.
const PORTAL_TOGGLES: { key: keyof PortalSettingsConfig; label: string; description: string }[] = [
  { key: 'esignature_enabled', label: 'E-signature', description: 'Businesses can draw and save a reusable signature to sign forms and agreements.' },
  { key: 'allow_document_upload', label: 'Document uploads', description: 'Businesses can upload documents, which land in the staff Document Review Queue.' },
  { key: 'allow_online_payment', label: 'Online payment', description: 'Businesses can pay invoices online from the portal.' },
];

/**
 * PortalSettingsEditor — the Org Settings card for the portal toggles.
 * Draft-vs-persisted working copy like the other editors; Save is enabled only
 * when a toggle differs. super_admin only (server-enforced).
 */
function PortalSettingsEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const cfgQuery = useQuery<PortalSettingsConfig>({
    queryKey: ['org-settings', 'portal-settings'],
    queryFn: ({ signal }) => api.get<PortalSettingsConfig>('/api/org-settings/portal-settings', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<PortalSettingsConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const persisted = cfgQuery.data;
  const value = draft ?? persisted ?? null;

  const dirty = draft !== null && persisted != null &&
    PORTAL_TOGGLES.some((t) => draft[t.key] !== persisted[t.key]);

  function toggle(key: keyof PortalSettingsConfig, checked: boolean) {
    setDraft({ ...(value as PortalSettingsConfig), [key]: checked });
  }

  async function save() {
    if (!value) return;
    setSaving(true);
    try {
      await api.put('/api/org-settings/portal-settings', { portal_settings: value });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'portal-settings'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Portal settings saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Portal features</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {cfgQuery.isLoading || !value ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {PORTAL_TOGGLES.map((t) => (
            <Checkbox
              key={t.key}
              label={t.label}
              description={t.description}
              checked={value[t.key]}
              disabled={!canEdit}
              onChange={(e) => toggle(t.key, e.target.checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

